import React, { useMemo } from "react";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import { ChatPreviewItem } from "./chat-preview-item.component";
import { cn } from "../../lib/utils/cn";
import { useConnectStore } from "../../store/Connect";
import { useRealtimeStore } from "@/lib/store/Realtime";

interface ChatPreviewListProps {
	channels: Tables<"message_channels">[];
	selectedChannelId?: string;
	onSelectChannel: (channel: Tables<"message_channels">) => void;
	maxItems?: number;
	userId: string;
	organizationId: string;
	className?: string;
	orgIdToClientNameMap?: Record<string, string>;
}

export const ChatPreviewList: React.FC<ChatPreviewListProps> = ({
	channels,
	selectedChannelId,
	onSelectChannel,
	maxItems,
	userId,
	organizationId,
	className,
	orgIdToClientNameMap,
}) => {
	const { brandsByOrgId } = useConnectStore((state) => ({
		brandsByOrgId: state.brandsByOrgId,
	}));
	const showOnlyUnread = useRealtimeStore((state) => state.showOnlyUnread);

	// Filter channels based on unread status if enabled
	const filteredChannels = useMemo(() => {
		if (showOnlyUnread) {
			return channels.filter(channel => (channel.unread_count || 0) > 0);
		}
		return channels;
	}, [channels, showOnlyUnread]);

	const displayChannels = useMemo(() => {
		return maxItems ? filteredChannels.slice(0, maxItems) : filteredChannels;
	}, [filteredChannels, maxItems]);

	const getOtherOrgName = (otherOrgId: string) => {
		const brandName = brandsByOrgId[otherOrgId || ""]?.name;
			
		// Check if brand name is default and use client name if available
		const clientName = orgIdToClientNameMap?.[otherOrgId || ""];

		const isDefaultBrandName = brandName === "Neue Praxis" || brandName === "Neues Labor" || (typeof brandName === 'string' && /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}'s (Praxis|Labor)/.test(brandName))
		
		return (isDefaultBrandName && clientName) ? clientName : brandName;
	};

	const getChannelTitle = (channel: Tables<"message_channels">) => {
		// For general channels
		if (channel.channel_type === "general") {
			const otherOrgId = channel.customer_org_id === organizationId 
				? channel.vendor_org_id 
				: channel.customer_org_id;
			const prefix = channel.customer_org_id === organizationId ? "Labor:" : "Kunde:";
			const displayName = getOtherOrgName(otherOrgId || "");
			return `${prefix} ${displayName || "Allgemeiner Chat"}`;
		} else if (channel.channel_type === "job_request" && !channel?.job_id) {
			return "Neue Auftragsanfrage";
		}
		
		// For job channels, show job title
		if (channel.job_title) {
			return channel.job_title;
		}
		
		// Fallback
		return "Chat";
	};

	const getChannelSubtitle = (channel: Tables<"message_channels">) => {
		// For job channels, show the organization name
		const otherOrgId = channel.customer_org_id === organizationId 
				? channel.vendor_org_id 
				: channel.customer_org_id;
		if (channel.job_id && channel.channel_type === "job") {
			return getOtherOrgName(otherOrgId || "") || "";
		} else if (channel.channel_type === "general") {
			if (channel.customer_org_id === organizationId) {
				return "Allgemeiner Chat mit Labor";
			} else {
				return "Allgemeiner Chat mit Kunde";
			}
		} else if (channel.channel_type === "job_request" && !channel?.job_id){
			return getOtherOrgName(otherOrgId || "");
		}
		return "";
	};

	if (displayChannels.length === 0) {
		return (
			<div className={cn("flex items-center justify-center py-8 text-gray-500 min-h-[100px]", className)}>
				<p className="text-sm">
					{showOnlyUnread ? "Keine ungelesenen Chats" : "Keine Chats gefunden"}
				</p>
			</div>
		);
	}

	return (
		<div className={cn("flex flex-col", className)}>
			{displayChannels.map((channel) => (
				<ChatPreviewItem
					key={channel.id}
					title={getChannelTitle(channel)}
					subtitle={getChannelSubtitle(channel)}
					messageAt={channel.last_message_at || ""}
					messageContent={channel.last_message_content as string || ""}
					isSender={channel.last_message_created_by === userId}
					active={channel.id === selectedChannelId}
					onClick={() => onSelectChannel(channel)}
					unreadCount={channel.unread_count || 0}
					jobId={channel.job_id || undefined}
					clientId={channel.job_client_id || undefined}
					channelType={channel.channel_type || undefined}
					weAreTheVendor={channel.vendor_org_id === organizationId}
				/>
			))}
		</div>
	);
};